import { supabase } from './supabase'

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'https://api.ohear.ai'

/**
 * Structured error thrown by `api()` when the server returns a non-2xx response.
 * `code` mirrors the API contract `error.code`; `requestId` is the upstream request id
 * (echoed back via `error.request_id`) and is the right value to surface in support tickets.
 */
export class ApiError extends Error {
  public readonly status: number
  public readonly code: string
  public readonly requestId?: string

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.requestId = requestId
  }
}

interface ApiErrorBody {
  error?: {
    code?: string
    message?: string
    request_id?: string
  }
}

/**
 * Thin fetch wrapper that injects the current Supabase access token and parses
 * the standard API error envelope. Returns `undefined` for 204 No Content.
 *
 * @example
 *   const project = await api<Project>('/api/v1/projects/' + id)
 *   await api('/api/v1/projects/' + id, { method: 'DELETE' })
 */
/** localStorage key for the actor-selected active org. Read on every API
 *  call and forwarded as `x-org-id` so the BE scopes data to that org
 *  without needing a token refresh. */
const ACTIVE_ORG_KEY = 'ohear.active-org-id'

function readActiveOrgId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY)
  } catch {
    return null
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const headers = new Headers(init?.headers)
  if (session) headers.set('Authorization', `Bearer ${session.access_token}`)
  if (init?.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')

  // Attach the SPA's chosen org for multi-org users (org switcher). Header
  // wins over the JWT's claimed org_id; the BE re-verifies membership so
  // an unauthorized id still 403s.
  const activeOrg = readActiveOrgId()
  if (activeOrg && !headers.has('x-org-id')) headers.set('x-org-id', activeOrg)

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (!res.ok) {
    const body: ApiErrorBody = await res.json().catch(() => ({}))
    throw new ApiError(
      res.status,
      body.error?.code ?? 'unknown',
      body.error?.message ?? res.statusText,
      body.error?.request_id,
    )
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/**
 * Standard envelope used by paginated list endpoints. Many `/api/v1/*` GET
 * routes return `{ items, next_cursor? }` (pagination-friendly) rather than a
 * bare array. This helper unwraps to the items array so call sites can keep
 * `useQuery<T[]>` ergonomics.
 *
 * @example
 *   const projects = await api<ListEnvelope<Project>>('/api/v1/projects').then(unwrapItems)
 */
export interface ListEnvelope<T> {
  items: T[]
  next_cursor?: string | null
}

export function unwrapItems<T>(env: ListEnvelope<T>): T[] {
  return env?.items ?? []
}
